import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import type { ITheme } from '@inter-ikea-kompis/types';
import type { RenderResult } from '@inter-ikea-kompis/web-component';

import {
	Choice as SkapaChoice,
	ChoiceItem as SkapaChoiceItem
} from '@ingka/choice-webc/passive.js';
import { Text } from '@ingka/common-styles-webc';

import SortListOptionsEnum from '../../enums/SortListOptionsEnum.js';
import type ISortListLocalizedInformation from '../../types/ISortListLocalizedInformation.js';
import type ISortListTranslations from '../../types/ISortListTranslations.js';
import LabelUtility from '../../utilities/LabelUtility.js';

/**
 * @event change When sort option is changed.
 */

export default class SortListCard extends KompisElement {
	// Theme
	@property({ type: Object, required: true })
	public theme!: ITheme;

	// Localized information. Deprecated - use "translations" instead.
	@property({ type: Object })
	public localizedInformation: ISortListLocalizedInformation | null = null;

	// The translations retrieved from translations service.
	@property({ type: Object })
	public translations: ISortListTranslations | null = null;

	// Selected option
	@property({ type: String })
	public selected: SortListOptionsEnum | null = null;

	public override async connectedCallback(): Promise<void> {
		this.customElements.define('skapa-choice', SkapaChoice);
		this.customElements.define('skapa-choice-item', SkapaChoiceItem);

		super.connectedCallback();
	}

	public override render(): RenderResult {
		// We want translations to be required, but since both props to provide translations are optional, this check is needed until localizedInformation is removed.
		if (!this.hasTranslations()) {
			// We can't throw an error because on the first render no props are set.
			return null;
		}

		return html`<style>
				h2 {
					margin-top: 0;
					margin-bottom: ${this.theme.spacing.space200 / 16}rem;
				}
				${Text.CSS}
			</style>
			${this.getHeading()}${this.getChoice()}`;
	}

	private hasTranslations(): boolean {
		return !!this.translations || !!this.localizedInformation?.translations;
	}

	private getTranslations(): ISortListTranslations {
		const translations = this.translations
			? this.translations
			: this.localizedInformation?.translations;

		return <ISortListTranslations>translations;
	}

	private getHeading(): RenderResult {
		return html`<h2 class="${Text.classnames({ heading: { m: true } })}">
			${this.getTranslations().pickingListSortTitle}
		</h2>`;
	}

	private getChoice(): RenderResult {
		return html`<skapa-choice subtle
			>${Object.values(SortListOptionsEnum).map((option) =>
				this.getChoiceItem(option)
			)}</skapa-choice
		> `;
	}

	private getChoiceItem(option: SortListOptionsEnum): RenderResult {
		const selectedOption = this.selected || SortListOptionsEnum.default;

		return html`
			<skapa-choice-item
				@choice-item-select="${() => {
					this.dispatchEvent(
						new CustomEvent('change', { bubbles: true, detail: { selected: option } })
					);
				}}"
				.selected="${selectedOption === option}"
			>
				<span slot="title">${LabelUtility.getLabel(option, this.getTranslations())}</span>
			</skapa-choice-item>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'skapa-choice': SkapaChoice;
		'skapa-choice-item': SkapaChoiceItem;
		'kompis-sort-list-card': SortListCard;
	}
}
